import React, { useEffect, useMemo, useState } from 'react';
import { Route, Switch, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { errorLocals } from 'src/locals';

import styles from 'src/app/App.module.scss';
import { reduxRootTypes } from 'src/types';
import { enumHelper, statusHelper, permissionHelper, routeHelper, storageHelper } from 'src/app/helpers';

import { axios, axiosc, axiosExport } from './utils';
import {
  contentsActions,
  eventActions,
  permissionActions,
  rootActions,
  unitsActions,
  userActions,
} from './redux/actions';
import {
  userService,
  eventService,
  objectiveService,
  ontologyService,
  incidentService,
  taskService,
  outcomeService,
  deliverableService,
  incidentFolderService,
  experimentationService,
  workPackageService,
  resourceService,
  injectionService,
  projectInputService,
  actionService,
  kanbanService,
  stateService,
  transactionService,
  permissionService,
  userRolePermissionService,
  fieldsService,
  observationTaskService,
  lillService,
  settingService,
  configurationChangeRequestService,
  troubleTicketService,
  testService,
  technicalSupportRequestService,
  battleRhythmService,
  decisiveConditionService,
  lineOfEffortService,
  planOrderReportProcessService,
  odTaskService,
  effectService,
  turnChartService,
  phaseService,
  projectService,
  coaService,
  riskService,
  veritabaniService,
} from './services';

import { LoginLayout, LicenseLayout } from './layouts';

import ETECubeSection from './components/ETECubeSection/ETECubeSection';
import { getPermissionKeyByUserRole } from './helpers/permissions.helper';
import { isDefined } from 'class-validator';
import RedirectPage from 'src/app/pages/RedirectPage/RedirectPage';
import { allODC2MReadPermissions, allPPMPermissions } from './components/Sidebar/sidebar.config';
import { PermissionEnum } from 'src/enums/permission.enum';
import MMUConnectionHandler from './components/Navbar/MMUConnectionHandler';
import WBConnectionHandler from './components/Navbar/WBConnectionHandler';
import EtecubeConnectionHandler from './components/Navbar/EtecubeConnectionHandler';
import HymotseConnectionHandler from './components/Navbar/HymotseConnectionHandler';
import { useTranslation } from 'react-i18next';
import { EventTimePreference } from 'src/enums/eventTimePreference.enum';
import moment from 'moment';
import { MINUTE, SECOND } from './constants/time.constant';
import { permissionEnums } from 'src/enums';
import LoginPage from './pages/LoginPage/LoginPage';
import { UserRoleType } from 'src/types/redux/user.redux.types';

declare global {
  interface BigInt {
    toJSON(): Number;
  }
}

BigInt.prototype.toJSON = function () {
  return Number(this);
};

const App = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { i18n } = useTranslation();

  const [isUserRequested, setIsUserRequested] = useState<boolean>(false);
  const [mainPage, setMainPage] = useState<string>('');

  const { eventId, eventList, hymotsDB, hymotseLogin, timePreference } = useSelector(
    (state: reduxRootTypes.RootStateType) => state.eventState,
  );

  const { userrolepermissions, localpermissions } = useSelector(
    (state: reduxRootTypes.RootStateType) => state.userRolePermissionState,
  );

  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);

  useEffect(() => {
    if (i18n.language !== userState.preferredLanguage) {
      i18n.changeLanguage(userState.preferredLanguage);
    }
  }, [i18n.language, userState.preferredLanguage]);

  useEffect(() => {
    const unit = storageHelper.getValueWithKey('weightUnit');

    if (unit) {
      dispatch(unitsActions.changeWeightUnit(Number(unit)));
    } else {
      dispatch(unitsActions.changeWeightUnit(1));
    }
  }, []);

  useEffect(() => {
    const token = storageHelper.getValueWithKey('token');
    const tokenc = storageHelper.getValueWithKey('tokenc');
    const preEventId = storageHelper.getValueWithKey('eventId');

    if (token && tokenc && preEventId) {
      dispatch(userActions.setToken({ token, tokenc }));
      axios.setToken(token);
      storageHelper.setValueWithKey('token', token);
      storageHelper.setValueWithKey('tokenc', tokenc);
      dispatch(eventActions.eventIdChanged(preEventId));
    } else {
      setIsUserRequested(true);
    }
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (timePreference === EventTimePreference.Simulation && hymotsDB) {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }

      (async () => {
        const {
          data: { rows: veritabani },
        } = await veritabaniService.veritabaniLookup({ blockSpinner: true });

        const currentDatabase = veritabani.find((v) => v.id === hymotsDB);
        const databaseTime = currentDatabase?.zaman.baslangicZamani;

        const dateUTC = moment(databaseTime).utc().format('YYYY-MM-DD HH:mm:ss');

        databaseTime && dispatch(eventActions.eventUnixTimeChanged(moment(dateUTC, 'YYYY-MM-DD HH:mm:ss').valueOf()));
      })();
    } else {
      dispatch(eventActions.eventUnixTimeChanged(new Date().valueOf()));
      intervalId = setInterval(() => {
        dispatch(eventActions.eventUnixTimeChanged(new Date().valueOf()));
      }, 1 * MINUTE);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [timePreference, hymotsDB]);

  const getTrainingObjectiveData = async () => {
    try {
      const trainingObjectiveListData = await objectiveService.lookupTrainingObjectiveTable({
        blockSpinner: true,
      });

      dispatch(contentsActions.trainingObjectiveList(trainingObjectiveListData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getOntologiesData = async () => {
    try {
      const ontologyData = await ontologyService.lookupOntologiesOfEvent(eventId);
      dispatch(contentsActions.ontologyList(ontologyData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getIncidentData = async () => {
    try {
      const incidentData = await incidentService.lookupIncidentTable({ blockSpinner: true });

      dispatch(contentsActions.incidentList(incidentData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getTaskData = async () => {
    try {
      const taskData = await taskService.lookupTaskTable({ blockSpinner: true });
      dispatch(contentsActions.taskList(taskData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getInjectionData = async () => {
    try {
      const injectionData = await injectionService.lookupInjectionTable({ blockSpinner: true });

      dispatch(contentsActions.injectionList(injectionData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectInputData = async () => {
    try {
      const projectInputData = await projectInputService.lookupProjectInputTable({ blockSpinner: true });

      dispatch(contentsActions.projectInputList(projectInputData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getActionData = async () => {
    try {
      const actionData = await actionService.lookupActionTable({ blockSpinner: true });
      dispatch(contentsActions.actionList(actionData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getKanbanData = async () => {
    try {
      const kanbanData = await kanbanService.lookupKanbanTable({ blockSpinner: true });
      dispatch(contentsActions.kanbanList(kanbanData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getPhases = async () => {
    try {
      const response = await phaseService.lookupPhase({});
      if (response?.data) {
        dispatch(contentsActions.phaseList(response.data.rows));
      }
    } catch (error) {
      console.error('Error fetching phase:', error);
    }
  };

  const getLineOfEfforts = async () => {
    try {
      const response = await lineOfEffortService.lookupLineOfEffort({});
      if (response?.data) {
        dispatch(contentsActions.lineOfEffortList(response.data.rows));
      }
    } catch (error) {
      console.error('Error fetching planOrderReportProcess:', error);
    }
  };

  const getPlanOrderProcesses = async () => {
    try {
      const response = await planOrderReportProcessService.lookupPlanOrderReportProcess({});

      if (response?.data) {
        dispatch(contentsActions.planOrderReportProcessList(response.data.rows));
      }
    } catch (error) {
      console.error('Error fetching planOrderReportProcess:', error);
    }
  };

  const getOutcomeData = async () => {
    try {
      const outcomeData = await outcomeService.lookupOutcomeTable({ blockSpinner: true });
      dispatch(contentsActions.outcomeList(outcomeData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getDeliverableData = async () => {
    try {
      const deliverableData = await deliverableService.lookupDeliverableTable({ blockSpinner: true });
      dispatch(contentsActions.deliverableList(deliverableData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getObservationTaskData = async () => {
    try {
      const observationTaskData = await observationTaskService.lookupObservationTaskTable({ blockSpinner: true });
      dispatch(contentsActions.observationTaskList(observationTaskData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getTestData = async () => {
    try {
      const testData = await testService.lookupTestTable({ blockSpinner: true });
      dispatch(contentsActions.testList(testData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getIncidentFolderData = async () => {
    try {
      const incidentFolderData = await incidentFolderService.lookupIncidentFolder({ blockSpinner: true });
      dispatch(contentsActions.incidentFolderList(incidentFolderData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getExperimentationData = async () => {
    const hymotsDbId = hymotsDB?.toString();
    try {
      const experimentationData = await experimentationService.lookupExperimentationTable({
        blockSpinner: true,
        value: hymotsDbId,
      });

      dispatch(contentsActions.experimentationList(experimentationData.data.rows));
    } catch (error) {
      hymotsDB;
      console.log(error);
    }
  };

  const getWorkPackageData = async () => {
    try {
      const workPackageData = await workPackageService.lookupWorkPackage({ blockSpinner: true });
      dispatch(contentsActions.workPackageList(workPackageData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getEventData = async () => {
    try {
      const eventData = await eventService.lookupEvent({ blockSpinner: true });

      dispatch(eventActions.eventList(eventData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getStateData = async () => {
    try {
      const stateData = await stateService.lookupState({ blockSpinner: true });

      dispatch(contentsActions.stateList(stateData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getTransactionData = async () => {
    try {
      const transactionData = await transactionService.lookupTransaction({ blockSpinner: true });

      dispatch(contentsActions.transactionList(transactionData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };
  const getResourceData = async () => {
    try {
      const resourceData = await resourceService.lookupResource({ blockSpinner: true });

      dispatch(contentsActions.resourceList(resourceData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getPermissionData = async () => {
    try {
      const permissionData = await permissionService.lookupPermission({ blockSpinner: true });

      dispatch(permissionActions.permissionList(permissionData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const allDatabaseRolesOfUser = useMemo<UserRoleType[]>(() => {
    const initialRolesById = new Map<string, UserRoleType>();

    userState.events.forEach((event) => {
      event.roles.forEach((role) => {
        initialRolesById.set(role.id, role);
      });
    });

    return Array.from(initialRolesById.values());
  }, [userState.events]);

  const getUserRolePermissionData = async () => {
    try {
      const userRolePermissionData = await userRolePermissionService.lookupUserRolePermission({
        blockSpinner: true,
      });

      dispatch(permissionActions.userRolePermissionList(userRolePermissionData.data.rows));

      const allPermissions = getPermissionKeyByUserRole(allDatabaseRolesOfUser, userRolePermissionData.data.rows);

      // Initial permissions for the user before any event is selected
      if (allPermissions.includes(PermissionEnum.EventCreate)) {
        dispatch(permissionActions.userLocalPermissionList([PermissionEnum.EventCreate]));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getFieldsData = async () => {
    try {
      const fieldsData = await fieldsService.lookupFields({
        blockSpinner: true,
      });
      dispatch(contentsActions.fieldsList(fieldsData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getCCRData = async () => {
    try {
      const ccrData = await configurationChangeRequestService.lookupConfigurationChangeRequest({
        blockSpinner: true,
      });
      dispatch(contentsActions.configurationChangeRequestList(ccrData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };
  const getTTData = async () => {
    try {
      const ttData = await troubleTicketService.lookupTroubleTicket({
        blockSpinner: true,
      });
      dispatch(contentsActions.troubleTicketList(ttData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getTSRData = async () => {
    try {
      const tsrData = await technicalSupportRequestService.lookupTechnicalSupportRequest({
        blockSpinner: true,
      });
      dispatch(contentsActions.technicalSupportRequestList(tsrData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getBattleRhythmData = async () => {
    try {
      const battleRhythmData = await battleRhythmService.lookupBattleRhythmTable({
        blockSpinner: true,
      });
      dispatch(contentsActions.battleRhythmList(battleRhythmData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getDecisiveConditionData = async () => {
    try {
      const decisiveCondition = await decisiveConditionService.lookupDecisiveCondition({ blockSpinner: true });
      dispatch(contentsActions.decisiveConditionList(decisiveCondition.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getOdTaskData = async () => {
    try {
      const taskData = await odTaskService.lookupTask({
        blockSpinner: true,
      });

      dispatch(contentsActions.odTaskList(taskData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getEffectData = async () => {
    try {
      const effectData = await effectService.lookupEffect({
        blockSpinner: true,
      });

      dispatch(contentsActions.effectList(effectData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getTurnChartData = async () => {
    try {
      const turnChartData = await turnChartService.lookupTurnChart({
        blockSpinner: true,
      });

      dispatch(contentsActions.turnChartList(turnChartData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectsData = async () => {
    try {
      const projectData = await projectService.lookupProject({ blockSpinner: true });

      dispatch(contentsActions.projectsList(projectData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  const getCoaData = async () => {
    try {
      const coaData = await coaService.lookupCoa({
        blockSpinner: true,
      });

      dispatch(contentsActions.coaList(coaData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (userState.token && userState.tokenc) {
      axios.setToken(userState.token);
      axios.setEventId(eventId);

      // axiosc.setEventId('5');
      axiosc.setToken(userState.tokenc);
      axiosExport.setToken(userState.token);
      axiosExport.setEventId(eventId);
      storageHelper.setValueWithKey('token', userState.token);
      storageHelper.setValueWithKey('tokenc', userState.tokenc);

      const getInformation = async () => {
        try {
          const { data } = await userService.getMe();

          if (!data) {
            axios.setToken(undefined);
            storageHelper.removeKeyPair('token');
            storageHelper.removeKeyPair('tokenc');
            dispatch(rootActions.resetReducerStatesToDefault());
            statusHelper.displayErrorMessage(t('errors.unknownError'));
          }

          const convertedData = enumHelper.convertUserServiceToRedux(data.user);

          dispatch(userActions.setInformation(convertedData));
        } catch (error) {
          if (error !== errorLocals.errorHandledError) {
            statusHelper.displayErrorMessage(t('errors.unknownError'));
          }
          axios.setToken(undefined);
          storageHelper.removeKeyPair('token');
          storageHelper.removeKeyPair('tokenc');
          dispatch(rootActions.resetReducerStatesToDefault());
        } finally {
          setIsUserRequested(true);
        }
      };
      getInformation();
    }
  }, [userState.token, userState.tokenc, eventId]);

  useEffect(() => {
    if (userState.token && userState.tokenc && eventId === '') {
      axios.setEventId(undefined);
      dispatch(permissionActions.userLocalPermissionList([]));
      dispatch(contentsActions.stateList([]));
      // closeSocket();
      routeHelper.handleRoute(history, '/');
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      const selectedRoles = userState.events.find((e) => e.id === eventId)?.roles;
      if (selectedRoles) {
        dispatch(userActions.setRoles(selectedRoles));

        const allPermissions = getPermissionKeyByUserRole(allDatabaseRolesOfUser, userrolepermissions);
        dispatch(permissionActions.userAllDatabasesPermissionList(allPermissions));

        // Get the user's permissions after an event is selected
        const permissionsOfUser = getPermissionKeyByUserRole(selectedRoles, userrolepermissions);
        dispatch(permissionActions.userLocalPermissionList(permissionsOfUser));

        if (hymotseLogin) setMainPage('Experimentation');
        else {
          setMainPage(permissionHelper.checkRoutePermissions(permissionsOfUser));
        }
      }
    } else {
      setMainPage('');
    }
  }, [eventList, eventId, userState.events, userrolepermissions]);

  useEffect(() => {
    if (eventId !== '') {
      getOntologiesData();
      getTransactionData();
      getFieldsData();
      getStateData();
    }
  }, [eventId]);

  useEffect(() => {
    if (userState.token) {
      getPermissionData();
      getUserRolePermissionData();
      getEventData();
    }
  }, [userState.token]);

  /*This use effect is for table problem */
  useEffect(() => {
    window.addEventListener('error', (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const licenseId = params.get('licenseId');

    if (licenseId === '956b57c2-69de-4534-a675-dc603fc5754e') {
      dispatch(eventActions.hymotseLogin(true));
    }
  }, [location.search, dispatch]);

  return (
    <div className={styles.app}>
      {/* Includes HYMOTS SOCKET, MMU, SURFER CONNECTION AND LISTENERS */}
      <MMUConnectionHandler />
      {/* Includes HYMOTS-C ETMEN, COMMANDS, NOTIFICATIONS etc. CONNECTION AND LISTENERS  */}
      <WBConnectionHandler />
      {/* Includes ETECUBE BACKEND CCR, TT, NOTIFICATIONS etc. CONNECTION AND LISTENERS */}
      <EtecubeConnectionHandler />

      {localpermissions.find((permission) => permission === PermissionEnum.HymotseRead) && (
        <>
          {/* Includes HYMOTS-E SOCKET CONNECTION */}
          <HymotseConnectionHandler />
        </>
      )}
      {/* <Router> */}
      {isUserRequested && (
        <>
          {' '}
          <Route exact path={['/Login', '/Register']}>
            <LoginLayout>
              <Switch>
                <Route exact path="/Login" component={LoginPage} />
                {/* <Route exact path="/Register" component={RegisterPage} /> */}
              </Switch>
            </LoginLayout>
          </Route>
          <Route exact path={['/License', '/Redirect']}>
            <LicenseLayout>
              <Switch>
                <Route exact path="/Redirect" component={RedirectPage} />
              </Switch>
            </LicenseLayout>
          </Route>
          <Route
            exact
            path={['/']}
            render={() => {
              return localpermissions?.length > 0 && mainPage ? (
                <Redirect to={`/${mainPage}`} />
              ) : isDefined(userState?.token) ? (
                <ETECubeSection />
              ) : (
                <Redirect to="/Login" />
              );
            }}
          />
          <Route
            exact
            path={[
              '/Ontology',
              '/ObjectiveManagement',
              '/FlowManagement',
              '/RealLifeSupport',
              '/Experimentation',
              '/Administration',
              '/Lill',
              '/SSM',
              '/PPM',
              '/ODC2M',
              '/COAM',
              '/HelpAndSupport',
              '/ProductLicenses',
            ]}
            render={() => {
              return isDefined(userState?.token) ? <ETECubeSection /> : <Redirect to="/Login" />;
            }}
          />
        </>
      )}
      {/* </Router> */}
      {/* <ProgressSpinner anyPendingRequest={anyPendingRequest} /> */}
    </div>
  );
};

export default App;
